/**
 * Form compartido para crear y editar proyectos. El modo cambia el label del
 * botón de submit y deshabilita `slug` en edit (la URL del proyecto depende
 * de él, cambiarlo rompe links externos).
 */
import { useForm, useWatch, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef, type ReactNode } from "react";
import { projectCreateSchema, type ProjectCreateInput } from "@axorial/shared";
import { CompanyPicker } from "./CompanyPicker";
import { PlaneProjectPicker } from "@/features/integrations/PlaneProjectPicker";
import { MattermostChannelPicker } from "@/features/integrations/MattermostChannelPicker";

/** Convierte texto a slug kebab-case ASCII para usar en URLs. */
function slugify(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "") // strip diacritics
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

const INTERNAL_COMPANY_ID = "co_axorial_internal";

interface ProjectFormProps {
  mode: "create" | "edit";
  defaultValues?: Partial<ProjectCreateInput>;
  onSubmit: (data: ProjectCreateInput) => void | Promise<void>;
  onCancel: () => void;
  submitting?: boolean;
}

const DEFAULT_VALUES: Partial<ProjectCreateInput> = {
  type: "client",
  status: "active",
  health: "unknown",
};

export function ProjectForm({
  mode,
  defaultValues,
  onSubmit,
  onCancel,
  submitting,
}: ProjectFormProps) {
  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors, isSubmitting: rhfSubmitting },
  } = useForm<ProjectCreateInput>({
    resolver: zodResolver(projectCreateSchema),
    defaultValues: { ...DEFAULT_VALUES, ...defaultValues },
  });

  const isSubmitting = submitting ?? rhfSubmitting;
  const projectType = useWatch({ control, name: "type" });
  const nameValue = useWatch({ control, name: "name" });
  const slugValue = useWatch({ control, name: "slug" });

  // Auto-generar slug desde el nombre mientras el usuario escribe — solo en
  // modo create y mientras el slug esté vacío o haya sido auto-generado.
  // Una vez el usuario lo edita manualmente, dejamos de sobreescribir.
  const slugAutoFromNameRef = useRef(true);
  useEffect(() => {
    if (mode !== "create") return;
    if (!slugAutoFromNameRef.current) return;
    const auto = slugify(nameValue ?? "");
    setValue("slug", auto, { shouldValidate: false });
  }, [mode, nameValue, setValue]);

  const onSlugManualEdit = () => {
    slugAutoFromNameRef.current = false;
  };

  // Si el usuario borra el slug, volvemos a auto-generar.
  useEffect(() => {
    if (mode !== "create") return;
    if (!slugValue) slugAutoFromNameRef.current = true;
  }, [mode, slugValue]);

  return (
    <form onSubmit={handleSubmit((d) => onSubmit(d))} className="card-elevated">
      <div className="p-6 space-y-4">
        <Field label="Nombre" error={errors.name?.message} required>
          <input
            className="input input-bordered w-full"
            placeholder="Ej. Migración portal cliente X"
            autoFocus={mode === "create"}
            {...register("name")}
          />
        </Field>

        <Field
          label="Slug"
          help={
            mode === "edit"
              ? "El slug no se puede cambiar — rompería links existentes."
              : "Auto-generado desde el nombre. Edita si quieres uno custom."
          }
          error={errors.slug?.message}
          required
        >
          <input
            className="input input-bordered w-full font-mono text-sm"
            placeholder="migracion-cliente-x"
            disabled={mode === "edit"}
            {...register("slug", { onChange: onSlugManualEdit })}
          />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Tipo">
            <select
              className="select select-bordered w-full"
              {...register("type", {
                onChange: (e) => {
                  // Si cambia a internal, auto-asignar la empresa interna virtual.
                  // Si cambia a client, limpiar para forzar selección.
                  if (e.target.value === "internal") {
                    setValue("clientCompanyId", INTERNAL_COMPANY_ID, {
                      shouldValidate: true,
                    });
                  } else if (e.target.value === "client") {
                    setValue("clientCompanyId", undefined, { shouldValidate: true });
                  }
                },
              })}
            >
              <option value="client">Cliente</option>
              <option value="internal">Interno</option>
            </select>
          </Field>
          <Field label="Estatus">
            <select className="select select-bordered w-full" {...register("status")}>
              <option value="discovery">Discovery</option>
              <option value="active">Activo</option>
              <option value="paused">Pausado</option>
              <option value="completed">Completado</option>
              <option value="archived">Archivado</option>
            </select>
          </Field>
        </div>

        {projectType === "client" && (
          <Field
            label="Empresa cliente"
            help="Busca por nombre. Crea la empresa primero desde /crm si no existe."
            error={errors.clientCompanyId?.message}
            required
          >
            <Controller
              control={control}
              name="clientCompanyId"
              render={({ field }) => (
                <CompanyPicker
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.clientCompanyId?.message}
                />
              )}
            />
          </Field>
        )}

        {projectType === "internal" && (
          <div className="rounded-md bg-base-200/60 px-3 py-2 text-xs text-base-content/65">
            Proyecto interno → se agrupa bajo{" "}
            <span className="font-medium text-base-content">Axorial (interno)</span>.
          </div>
        )}

        <Field label="Descripción" error={errors.description?.message}>
          <textarea
            rows={3}
            className="textarea textarea-bordered w-full"
            placeholder="Qué hace, para quién, qué problema resuelve."
            {...register("description")}
          />
        </Field>

        <Divider>Integraciones</Divider>

        <Field label="Proyecto en Plane" help="Se sincroniza cada 15 minutos automáticamente.">
          <Controller
            control={control}
            name="planeProjectId"
            render={({ field }) => (
              <PlaneProjectPicker value={field.value} onChange={field.onChange} />
            )}
          />
        </Field>

        <Field
          label="Canal de Mattermost del proyecto"
          help="Canal específico de este proyecto (NO el global de standups). El bot debe ser miembro del canal."
        >
          <Controller
            control={control}
            name="mattermostChannelId"
            render={({ field }) => (
              <MattermostChannelPicker value={field.value} onChange={field.onChange} />
            )}
          />
        </Field>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-4 border-t border-base-300/60">
          <button type="button" className="btn btn-ghost" onClick={onCancel}>
            Cancelar
          </button>
          <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
            {isSubmitting && <span className="loading loading-spinner loading-xs" />}
            {mode === "create" ? "Crear proyecto" : "Guardar cambios"}
          </button>
        </div>
      </div>
    </form>
  );
}

function Field({
  label,
  error,
  help,
  required,
  children,
}: {
  label: string;
  error?: string;
  help?: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="form-control w-full block">
      <div className="label py-1">
        <span className="label-text text-sm font-medium">
          {label}
          {required && <span className="text-error ml-0.5">*</span>}
        </span>
      </div>
      {children}
      {error && <p className="text-xs text-error mt-1.5 leading-relaxed">{error}</p>}
      {!error && help && (
        <p className="text-xs text-base-content/55 mt-1.5 leading-relaxed">{help}</p>
      )}
    </label>
  );
}

function Divider({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center gap-3 pt-2">
      <span className="text-xs font-semibold uppercase tracking-wider text-base-content/55">
        {children}
      </span>
      <span aria-hidden className="flex-1 h-px bg-base-300/60" />
    </div>
  );
}
